import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAlarmInfiniteList, alarmKeys, alarmInfiniteKey } from "../../../hooks/useAlarm";
import { markAlarmAsRead, markAllAlarmsAsRead } from "../../../utils/alarmService";
import "./css/storealarmmodal.css";
import { formatDateTime } from "../../../utils/commonService";
import { IoCaretDown } from "../../../components/icons";

const StoreAlarmModal = ({ onClose, buttonRef }) => {
  const [activeTab, setActiveTab] = useState("전체");
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const memberId = JSON.parse(localStorage.getItem("user")).memberId;
  const {
    data: alarmPages,
    isLoading: loading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAlarmInfiniteList(memberId, activeTab);

  // 탭별 분류와 주문/취소 알람 제외는 서버에서 처리한다.
  const alarmData = alarmPages?.pages.flatMap((page) => page.content) ?? [];

  const tabs = ["전체", "상품", "네고", "리뷰", "고객센터", "기타"];
  
  const getCategoryFromAlarmType = (alarmType) => {
    // 백엔드에서 이미 한글로 오는 경우
    if (alarmType === "할인" || alarmType === "네고")  return "상품";
    if (alarmType === "네고 요청") return "네고";
    if (alarmType === "리뷰") return "리뷰";
    if (alarmType === "상품신고" || alarmType === "리뷰신고" || alarmType === "가맹신고" || alarmType === "문의") return "고객센터";
    if (alarmType === "상품 제재" || alarmType === "리뷰 숨김" || alarmType === "구독 결제" || alarmType === "기타") return "기타";
  };

  const transformedAlarms = alarmData.map((alarm, index) => ({
    id: index + 1,
    alarmIdx: alarm.alarmIdx, // 백엔드에서 받은 alarmIdx
    category: getCategoryFromAlarmType(alarm.alarmType),
    content: alarm.content || "알림 내용이 없습니다.",
    time: formatDateTime(alarm.sendDateTime),
    sendDateTime: alarm.sendDateTime,
    alarmLink: alarm.alarmLink,
    alarmType: alarm.alarmType,
    isUnread: !alarm.readDateTime, // readDateTime이 null이면 읽지 않은 알림
  }));

  const unreadAlarms = transformedAlarms
    .filter((alarm) => alarm.isUnread)
    .sort((a, b) => new Date(b.sendDateTime) - new Date(a.sendDateTime));

  const readAlarms = transformedAlarms
    .filter((alarm) => !alarm.isUnread)
    .sort((a, b) => new Date(b.sendDateTime) - new Date(a.sendDateTime));

  const handleMarkAllRead = async () => {
    try {
      await markAllAlarmsAsRead(memberId);
      queryClient.setQueriesData(
        { queryKey: alarmInfiniteKey(memberId) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              content: page.content.map((a) => ({
                ...a,
                readDateTime: a.readDateTime ?? new Date().toISOString(),
              })),
            })),
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: alarmKeys.list(memberId) });
      queryClient.invalidateQueries({ queryKey: alarmKeys.unread() });
    } catch {
      // silent
    }
  };

  const handleAlarmClick = async (alarm) => {
    // 알림 읽음 처리 API 호출
    try {
      if (alarm.alarmIdx && alarm.isUnread) {
        await markAlarmAsRead(alarm.alarmIdx);

        // infinite 캐시 즉시 업데이트
        queryClient.setQueriesData(
          { queryKey: alarmInfiniteKey(memberId) },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                content: page.content.map((a) =>
                  a.alarmIdx === alarm.alarmIdx
                    ? { ...a, readDateTime: new Date().toISOString() }
                    : a
                ),
              })),
            };
          }
        );

        // TopNav 뱃지용 list 캐시 무효화
        queryClient.invalidateQueries({ queryKey: alarmKeys.list(memberId) });
        queryClient.invalidateQueries({ queryKey: alarmKeys.unread() });
      }
    } catch (error) {
      console.error("알림 읽음 처리 실패:", error);
    }

    onClose();

    // 알람 타입에 따라 페이지 이동 및 상태 전달
    switch (alarm.alarmType) {
      case "리뷰":
        // 리뷰 페이지로 이동하고 reviewIdx 전달
        navigate("/store/review", {
          state: {
            openModal: true,
            reviewIdx: alarm.alarmLink,
          },
        });
        break;

      case "상품신고":
        // 상품관리 페이지로 이동하고 productCode 전달
        navigate("/store/product", {
          state: {
            openModal: true,
            productCode: alarm.alarmLink,
          },
        });
        break;

      case "리뷰신고":
        // 리뷰 페이지로 이동하고 reviewIdx 전달 (신고된 리뷰)
        navigate("/store/review", {
          state: {
            openModal: true,
            reviewIdx: alarm.alarmLink,
          },
        });
        break;

      case "가맹신고":
        // 스토어 신고는 마이페이지나 대시보드로 이동 (필요에 따라 수정)
        navigate("/store/mypage")
        break;

      case "네고":
        // 네고 페이지로 이동
        navigate("/store/negotiation", {
          state: {
            activeTab: "negotiation",
          }
        });
        break;

      case "네고 요청":
        // 네고 요청 페이지로 이동
        navigate("/store/negotiation", {
          state: {
            openModal: true,
            activeTab: "buyer-request",
          },
        });
        break;

      case "상품 제재":
        // 상품관리 페이지로 이동하고 productCode 전달
        navigate("/store/product", {
          state: {
            openModal: true,
            productCode: alarm.alarmLink,
          },
        });
        break;

      case "리뷰 숨김":
        // 리뷰 페이지로 이동
        navigate("/store/review");
        break;

      case "할인":
        // 할인 관리 페이지로 이동
        navigate("/store/sale")
        break;

      case "문의":
        // 문의 페이지로 이동
        navigate("/store/questions", {
          state: {
            openModal: true,
            questionIdx: alarm.alarmLink,
          },
        });
        break;

      default:
        break;
    }
  };

  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // 알람 버튼 클릭은 제외 (버튼 자체의 onClick이 토글을 처리)
        if (buttonRef?.current && buttonRef.current.contains(event.target)) {
          return;
        }
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, buttonRef]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const atTop = scrollTop === 0 && e.deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
      if (atTop || atBottom) {
        e.preventDefault();
      }
      e.stopPropagation();
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <div className="storealarmmodal-panel" ref={panelRef}>
      <div className="storealarmmodal-tabs">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`storealarmmodal-tab-button ${
              activeTab === tab ? "storealarmmodal-tab-active" : ""
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>
      {unreadAlarms.length > 0 && (
        <div className="storealarmmodal-toolbar">
          <span className="storealarmmodal-unread-count">읽지 않은 알람 {unreadAlarms.length}개</span>
          <button className="storealarmmodal-readall-btn" onClick={handleMarkAllRead}>
            전체 읽음
          </button>
        </div>
      )}

      <div className="storealarmmodal-list-container" ref={listRef}>
        {loading ? (
          <div className="storealarmmodal-empty">로딩 중...</div>
        ) : unreadAlarms.length === 0 && readAlarms.length === 0 ? (
          <div className="storealarmmodal-empty">알람이 없습니다.</div>
        ) : (
          <>
            {/* 읽지 않은 알람 */}
            {unreadAlarms.length > 0 && (
              <>
                {unreadAlarms.map((alarm) => (
                  <div
                    key={alarm.id}
                    className="storealarmmodal-item"
                    onClick={() => handleAlarmClick(alarm)}
                  >
                    <div className="storealarmmodal-item-header">
                      <span className="storealarmmodal-item-category">
                        {alarm.category}
                      </span>
                      <span
                        className="storealarmmodal-item-time"
                        style={{ fontWeight: "bold" }}
                      >
                        {alarm.time}
                      </span>
                    </div>
                    <div
                      className="storealarmmodal-item-content"
                      style={{ fontWeight: "bold" }}
                    >
                      {alarm.content}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* 구분선 */}
            {unreadAlarms.length > 0 && readAlarms.length > 0 && (
              <div className="storealarmmodal-divider">
                <span className="storealarmmodal-divider-text">읽은 알람</span>
              </div>
            )}

            {/* 읽은 알람 */}
            {readAlarms.length > 0 && (
              <>
                {readAlarms.map((alarm) => (
                  <div
                    key={alarm.id}
                    className="storealarmmodal-item storealarmmodal-item-read"
                    onClick={() => handleAlarmClick(alarm)}
                  >
                    <div className="storealarmmodal-item-header">
                      <span className="storealarmmodal-item-category">
                        {alarm.category}
                      </span>
                      <span className="storealarmmodal-item-time">
                        {alarm.time}
                      </span>
                    </div>
                    <div className="storealarmmodal-item-content">
                      {alarm.content}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {hasNextPage && (
          <button
            className="storealarmmodal-more-btn"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "로딩 중..." : <><div>더보기</div><IoCaretDown /></>}
          </button>
        )}
      </div>
    </div>
  );
};

export default StoreAlarmModal;
